import { Injectable, EnvironmentInjector, createEnvironmentInjector } from '@angular/core';
import {
    TranslateService,
    TranslateStore,
    TRANSLATE_SERVICE_CONFIG,
    InterpolationParameters,
    Translation,
} from '@ngx-translate/core';
import { catchError, Observable, throwError } from 'rxjs';
import { shareReplay, switchMap, take } from 'rxjs/operators';

/**
 * Provides translation functionality similar to ngx-translate's TranslateService
 * but allows retrieving translations for an explicitly specified language.
 *
 * The requested translations are resolved independently of the application's
 * currently active language and therefore do not influence the global language state.
 *
 * The API mirrors TranslateService#get(), with an additional `lang` parameter
 * defining the language to translate in.
 *
 * Intended for use cases where translations must be fetched in a fixed or
 * externally defined language (e.g., data processing or multilingual content).
 */

@Injectable({ providedIn: 'root' })
export class TranslateInService {
    private readonly t: TranslateService;
    private readonly langCache = new Map<string, Observable<unknown>>();

    constructor(
        envInjector: EnvironmentInjector,
        appTranslate: TranslateService, // nur um fallbackLang zu übernehmen (optional)
    ) {
        const childInjector = createEnvironmentInjector(
            [
                { provide: TranslateStore, useFactory: (): TranslateStore => new TranslateStore() },
                { provide: TranslateService, useClass: TranslateService },
                {
                    provide: TRANSLATE_SERVICE_CONFIG,
                    useValue: {
                        extend: false,
                        fallbackLang: appTranslate.getFallbackLang(),
                    },
                },
            ],
            envInjector,
        );

        this.t = childInjector.get(TranslateService);
    }

    // --- Overloads: wie TranslateService.get, nur mit lang davor ---

    /**
     * Retrieves translation(s) in the specified language.
     * Same API as {@link TranslateService#get}, with `lang` added as the first parameter.
     *
     * @see TranslateService#get
     *
     * @param lang The language to get the translation(s) for.
     * @param key The key(s) of the translation to be retrieved.
     * @param interpolateParams Optional parameters for interpolation.
     * @returns An Observable emitting the resolved translation(s).
     */
    get(lang: string, key: string | string[], interpolateParams?: InterpolationParameters): Observable<Translation> {
        return this.ensureLang(lang).pipe(switchMap(() => this.t.get(key, interpolateParams).pipe(take(1))));
    }

    private ensureLang(lang: string): Observable<unknown> {
        const cached = this.langCache.get(lang);
        if (cached) return cached;

        const load$ = this.t.use(lang).pipe(
            take(1),
            shareReplay({ bufferSize: 1, refCount: false }),
            catchError((err) => {
                this.langCache.delete(lang);
                return throwError(() => err);
            }),
        );

        this.langCache.set(lang, load$);
        return load$;
    }
}
